import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle,
  Download,
  Filter,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  adminListPlays,
  adminDeliverPlay,
  adminExportPlaysCsv,
  adminUpdatePlay,
  adminDeletePlay,
  adminDeleteAllPlays,
  type AdminPlay,
  type PlaysFilters,
  type UpdatePlayBody,
} from "../../lib/api";
import { Modal } from "../../components/ui/Modal";
import { HelpPopover } from "../../components/admin/forms/HelpPopover";
import { useAdminCtx } from "./AdminLayout";
import { useDialogs } from "../../hooks/useDialogs";

const PAGE_SIZE = 25;

export default function Ganadores() {
  const { sucursalIdEfectiva, user } = useAdminCtx();
  const dialogs = useDialogs();
  const [rows, setRows] = useState<AdminPlay[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Reorg UX 2026-06: es vista de trabajo, no exploratoria. Default
  // "solo pendientes" (delivered:false) porque la tarea central es ver
  // qué premios quedan por entregar; y filtros visibles de entrada (no
  // escondidos detrás de un click).
  const [filters, setFilters] = useState<PlaysFilters>({ delivered: false });
  const [showFilters, setShowFilters] = useState(true);
  const [deliveringId, setDeliveringId] = useState<number | null>(null);
  const [editing, setEditing] = useState<AdminPlay | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  // super_admin lee y AHORA también puede mutar (incluye limpiar
  // historial bulk). El backend valida que opere sobre una sucursal
  // definida (withTenant + as_sucursal). El user pidió poder limpiar
  // su historial estando logueado como super_admin.
  const canMutate = user.role === "admin" || user.role === "super_admin";

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminListPlays(
        { ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
        sucursalIdEfectiva,
      );
      setRows(result.rows);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filters, page, sucursalIdEfectiva]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  async function handleDeliver(p: AdminPlay) {
    const notes = await dialogs.prompt({
      title: "Marcar entregado",
      message: `Premio: ${p.prize_name_snapshot}\n\nNotas opcionales sobre la entrega:`,
      placeholder: "Ej: retiró Juan Pérez 14:30",
      confirmLabel: "Marcar entregado",
    });
    // Cancelar (Escape o botón Cancelar) → notes === null → no entregar.
    if (notes === null) return;
    setDeliveringId(p.id);
    try {
      await adminDeliverPlay(p.id, notes || undefined, sucursalIdEfectiva);
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    } finally {
      setDeliveringId(null);
    }
  }

  async function handleExport() {
    try {
      await adminExportPlaysCsv(filters, sucursalIdEfectiva);
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  async function handleDelete(p: AdminPlay) {
    const ok = await dialogs.confirm({
      title: "Borrar jugada",
      message: `Premio: ${p.prize_name_snapshot}\nFecha: ${formatDate(p.created_at)}\n\nEsta acción NO se puede deshacer.`,
      confirmLabel: "Borrar",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await adminDeletePlay(p.id, sucursalIdEfectiva);
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  async function handleSaveEdit(id: number, body: UpdatePlayBody) {
    try {
      await adminUpdatePlay(id, body, sucursalIdEfectiva);
      setEditing(null);
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  // Borra TODOS los plays de la sucursal. Solo se llama tras la
  // confirmación del modal (typing "BORRAR"). El backend también
  // valida el ?confirm=BORRAR como segunda barrera contra accidentes.
  async function handleClearAll() {
    setClearingAll(true);
    try {
      const res = await adminDeleteAllPlays(sucursalIdEfectiva);
      setConfirmClearOpen(false);
      await dialogs.alert({
        title: "Historial limpio",
        message: `Se borraron ${res.deleted} jugadas.`,
        variant: "success",
      });
      setPage(0);
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    } finally {
      setClearingAll(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-end gap-4">
        <h1 className="sr-only">
          Jugadas
          <HelpPopover label="Qué hacen las acciones" align="left">
            {"\"Entregar\" = marca que ya le diste el premio físico al cliente " +
              "(con timestamp + tu email). Sirve para saber cuáles quedan " +
              "pendientes de retirar.\n\n" +
              "\"Editar\" arregla datos cargados mal (nombre, teléfono).\n\n" +
              "\"Borrar\" saca la jugada del historial — usalo si fue de prueba " +
              "o el cliente pidió que borres sus datos." +
              (!canMutate
                ? "\n\nEstás logueado como super_admin: podés ver el historial " +
                  "de cualquier sucursal pero no editar/borrar jugadas ajenas. " +
                  "Cada admin de sucursal mantiene su propio historial."
                : "")}
          </HelpPopover>
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters((s) => !s)}
            data-tour="ganadores-filters"
            className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 hover:bg-white/20"
          >
            <Filter className="h-4 w-4" />
            Filtros
          </button>
          <button
            onClick={handleExport}
            data-tour="ganadores-export"
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 hover:bg-emerald-500"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </button>
          {canMutate && total > 0 && (
            <button
              onClick={() => setConfirmClearOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-red-600/80 px-4 py-2 text-sm hover:bg-red-500"
              title="Borra TODAS las jugadas de tu sucursal"
            >
              <Trash2 className="h-4 w-4" />
              Limpiar historial
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <FiltersPanel
          value={filters}
          onChange={(f) => {
            setFilters(f);
            setPage(0);
          }}
        />
      )}

      {error && (
        <div className="mb-4 rounded-lg bg-red-500/15 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin opacity-50" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-white/5 p-8 text-center opacity-70">
          {filters.delivered === false
            ? "No hay premios pendientes de entrega."
            : "No hay jugadas que coincidan con los filtros."}
        </div>
      ) : (
        <>
          <div
            className="overflow-x-auto rounded-lg bg-white/5"
            data-tour="ganadores-list"
          >
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 text-left opacity-70">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Juego</th>
                  <th className="px-3 py-2">Premio</th>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2 w-48">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-3 py-2 text-xs opacity-80">
                      {formatDate(p.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-white/10 px-2 py-0.5 text-xs">
                        {p.game_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {p.prize_name_snapshot}
                      {p.is_consolation === 1 && (
                        <span className="ml-2 rounded bg-yellow-500/20 px-1.5 py-0.5 text-xs text-yellow-300">
                          consolation
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {p.customer_name ? (
                        <div>
                          <div>{p.customer_name}</div>
                          {p.customer_phone && (
                            <div className="opacity-60">{p.customer_phone}</div>
                          )}
                        </div>
                      ) : (
                        <span className="opacity-40">sin datos</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {p.is_consolation === 1 ? (
                        <span className="opacity-40">—</span>
                      ) : p.delivered === 1 ? (
                        <div>
                          <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-300">
                            Entregado
                          </span>
                          {p.delivered_at && (
                            <div className="mt-1 text-xs opacity-60">
                              {formatDate(p.delivered_at)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="rounded bg-orange-500/20 px-2 py-0.5 text-xs text-orange-300">
                          Pendiente
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
                        {p.is_consolation !== 1 && p.delivered === 0 && canMutate && (
                          <button
                            onClick={() => handleDeliver(p)}
                            disabled={deliveringId === p.id}
                            className="flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs hover:bg-emerald-500 disabled:opacity-50"
                          >
                            {deliveringId === p.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <CheckCircle className="h-3 w-3" />
                            )}
                            Entregar
                          </button>
                        )}
                        {canMutate && (
                          <>
                            <button
                              onClick={() => setEditing(p)}
                              className="rounded p-1 hover:bg-white/10"
                              title="Editar datos del cliente"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(p)}
                              className="rounded p-1 text-red-400 hover:bg-red-500/15"
                              title="Borrar jugada"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm">
            <div className="opacity-70">
              {total} jugadas total — página {page + 1} de {totalPages}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded p-2 hover:bg-white/10 disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded p-2 hover:bg-white/10 disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}

      <Modal open={editing !== null} onClose={() => setEditing(null)} closeOnBackdrop>
        {editing && (
          <PlayEditForm
            play={editing}
            onCancel={() => setEditing(null)}
            onSubmit={(body) => handleSaveEdit(editing.id, body)}
          />
        )}
      </Modal>

      <Modal
        open={confirmClearOpen}
        onClose={() => !clearingAll && setConfirmClearOpen(false)}
      >
        <ClearAllConfirm
          total={total}
          loading={clearingAll}
          onCancel={() => setConfirmClearOpen(false)}
          onConfirm={handleClearAll}
        />
      </Modal>
    </div>
  );
}

// Modal de confirmación destructiva. Pide tipear "BORRAR" para
// habilitar el botón — patrón estándar para acciones irreversibles
// (similar al "type the repo name to delete" de GitHub). El backend
// también requiere ?confirm=BORRAR, así que es defensa en profundidad.
function ClearAllConfirm({
  total,
  loading,
  onCancel,
  onConfirm,
}: {
  total: number;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const enabled = typed.trim().toUpperCase() === "BORRAR";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-red-400" />
        <div>
          <h2 className="text-xl font-bold text-red-300">
            Limpiar historial completo
          </h2>
          <p className="mt-2 text-sm opacity-80">
            Vas a borrar <strong>{total}</strong> jugadas de tu sucursal
            de forma permanente. Esto incluye los datos del cliente,
            estado de entregado y notas. <strong>No se puede deshacer.</strong>
          </p>
          <p className="mt-2 text-xs opacity-60">
            Exportá el CSV antes si querés un respaldo. Queda registrado en
            el audit log.
          </p>
        </div>
      </div>

      <label className="flex flex-col gap-2 text-sm">
        <span className="opacity-80">
          Para confirmar, escribí <strong className="text-red-300">BORRAR</strong>{" "}
          en mayúsculas:
        </span>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          placeholder="BORRAR"
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-red-400"
        />
      </label>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 rounded-lg bg-white/10 py-3 font-bold hover:bg-white/20 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!enabled || loading}
          className="flex-1 rounded-lg bg-red-600 py-3 font-bold transition disabled:cursor-not-allowed disabled:opacity-30"
        >
          {loading ? "Borrando…" : `Borrar ${total} jugadas`}
        </button>
      </div>
    </div>
  );
}

function PlayEditForm({
  play,
  onSubmit,
  onCancel,
}: {
  play: AdminPlay;
  onSubmit: (body: UpdatePlayBody) => Promise<void>;
  onCancel: () => void;
}) {
  const [nombre, setNombre] = useState(play.customer_name ?? "");
  const [telefono, setTelefono] = useState(play.customer_phone ?? "");
  const [doc, setDoc] = useState(play.customer_doc ?? "");
  const [email, setEmail] = useState(play.customer_email ?? "");
  const [ticket, setTicket] = useState(play.customer_ticket ?? "");
  const [notes, setNotes] = useState(play.notes ?? "");
  const [saving, setSaving] = useState(false);

  // Cada campo va como null si está vacío (borra el valor), string si tiene.
  const toBody = (s: string): string | null => (s.trim() === "" ? null : s.trim());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        customer_name: toBody(nombre),
        customer_phone: toBody(telefono),
        customer_doc: toBody(doc),
        customer_email: toBody(email),
        customer_ticket: toBody(ticket),
        notes: toBody(notes),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="flex items-center gap-2 text-2xl font-bold">
        Editar datos del cliente
        <HelpPopover label="Cómo borrar un valor" align="left">
          Dejá un campo vacío para borrar su valor.
        </HelpPopover>
      </h2>
      <p className="text-sm opacity-70">
        Jugada del {formatDate(play.created_at)} · Premio:{" "}
        <strong>{play.prize_name_snapshot}</strong>
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Nombre" value={nombre} onChange={setNombre} />
        <Field label="Teléfono" value={telefono} onChange={setTelefono} />
        <Field label="Cédula" value={doc} onChange={setDoc} />
        <Field label="Email" value={email} onChange={setEmail} type="email" />
        <Field label="Ticket" value={ticket} onChange={setTicket} />
      </div>
      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Notas internas</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      </label>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-lg bg-white/10 py-3 font-bold hover:bg-white/20"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold disabled:opacity-50"
        >
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="opacity-80">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
      />
    </label>
  );
}

function FiltersPanel({
  value,
  onChange,
}: {
  value: PlaysFilters;
  onChange: (f: PlaysFilters) => void;
}) {
  function update<K extends keyof PlaysFilters>(k: K, v: PlaysFilters[K]) {
    onChange({ ...value, [k]: v });
  }

  function dateToUnix(d: string): number | undefined {
    if (!d) return undefined;
    return Math.floor(new Date(d).getTime() / 1000);
  }

  function unixToDate(u: number | undefined): string {
    if (!u) return "";
    return new Date(u * 1000).toISOString().slice(0, 10);
  }

  return (
    <div className="mb-4 grid grid-cols-2 gap-3 rounded-lg bg-white/5 p-4 sm:grid-cols-4">
      <label className="flex flex-col gap-1 text-xs">
        <span className="opacity-70">Desde</span>
        <input
          type="date"
          value={unixToDate(value.desde)}
          onChange={(e) => update("desde", dateToUnix(e.target.value))}
          className="rounded bg-white/10 px-2 py-1 text-sm outline-none"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="opacity-70">Hasta</span>
        <input
          type="date"
          value={unixToDate(value.hasta)}
          onChange={(e) => update("hasta", dateToUnix(e.target.value))}
          className="rounded bg-white/10 px-2 py-1 text-sm outline-none"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="opacity-70">Juego</span>
        <select
          value={value.game_type ?? ""}
          onChange={(e) =>
            update(
              "game_type",
              e.target.value === ""
                ? undefined
                : (e.target.value as "ruleta" | "raspadita" | "memoria"),
            )
          }
          className="rounded bg-white/10 px-2 py-1 text-sm outline-none"
        >
          <option value="">Todos</option>
          <option value="ruleta">Ruleta</option>
          <option value="raspadita">Raspadita</option>
          <option value="memoria">Memoria</option>
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="opacity-70">Entregado</span>
        <select
          value={value.delivered === undefined ? "" : String(value.delivered)}
          onChange={(e) =>
            update(
              "delivered",
              e.target.value === "" ? undefined : e.target.value === "true",
            )
          }
          className="rounded bg-white/10 px-2 py-1 text-sm outline-none"
        >
          <option value="">Todos</option>
          <option value="true">Sí</option>
          <option value="false">No</option>
        </select>
      </label>
    </div>
  );
}

function formatDate(unix: number): string {
  const d = new Date(unix * 1000);
  return d.toLocaleString("es-PY", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
